import { useEffect } from "react";
import { Col, FloatingLabel, Form } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import CountryProvince from "../CountryProvince";

const CityProvinceCountrySelector = ({ formNamespace, disabled, readOnly }) => {
    const { control, setValue, watch, clearErrors } = useFormContext();
    const { city, setSelectState, handleCity, handleProvince, newHandleCountry } = CountryProvince();
    const cityList = city?.data?.map((a) => ({ name: a.city, id: a.id })) || [];
    const selectedCity = useWatch({ control, name: `${formNamespace}.city` }) || [];
    const selectedCityId = selectedCity[0]?.id;

    useEffect(() => {
        const updateProvinceList = async () => {
            if (selectedCityId) {
                const pList = await handleProvince(selectedCityId);
                const provinces = pList.data.data || [];
                setValue(`${formNamespace}.provinceList`, pList);
                setValue(`${formNamespace}.cityId`, selectedCityId);
                setValue(`${formNamespace}.cityName`, selectedCity[0].name);
                clearErrors([
                    `${formNamespace}.province`,
                    `${formNamespace}.country`
                ]);

                if (provinces.length === 1) {
                    const onlyProvince = provinces[0];
                    setValue(`${formNamespace}.province`, onlyProvince.provineStateID);
                    setValue(`${formNamespace}.provinceName`, onlyProvince.provineSateName);
                    setSelectState(onlyProvince.provineStateID);
                    const cList = await newHandleCountry(onlyProvince.provineStateID);
                    const countries = cList.data.data || [];
                    setValue(`${formNamespace}.countryList`, cList);
                    if (countries.length === 1) {
                        setValue(`${formNamespace}.country`, countries[0].countryId);
                    } else {
                        setValue(`${formNamespace}.country`, "");
                    }
                }
            } else {
                setValue(`${formNamespace}.provinceList`, { data: [] });
                setValue(`${formNamespace}.countryList`, { data: [] });
                setValue(`${formNamespace}.province`, "");
                setValue(`${formNamespace}.country`, "");
            }
        };

        updateProvinceList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCityId]);

    const handleProvinceChange = async (e) => {
        const provinceVal = e.target.value;
        setSelectState(provinceVal);
        setValue(`${formNamespace}.province`, provinceVal);
        clearErrors(`${formNamespace}.province`);
        const provinceName = (watch(`${formNamespace}.provinceList`)?.data?.data).find((item) => item.provineStateID == provinceVal).provineSateName
        setValue(`${formNamespace}.provinceName`, provinceName);
        handleCity(e);
        if (provinceVal !== "Select" && provinceVal !== "") {
            const cList = await newHandleCountry(provinceVal);
            const countries = cList.data.data || [];
            setValue(`${formNamespace}.countryList`, cList);
            if (countries.length === 1) {
                setValue(`${formNamespace}.country`, countries[0].countryId);
            }
        } else {
            setValue(`${formNamespace}.countryList`, { data: [] });
            setValue(`${formNamespace}.country`, "");
        }
    };

    const handleCountryChange = (e) => {
        const value = e.target.value;
        if (value !== "Select" && value !== "") {
            setValue(`${formNamespace}.country`, value);
            clearErrors(`${formNamespace}.country`);
        }
    };

    return (
        <>
            <Col sm={2}>
                <Form.Group controlId="city">
                    <Controller
                        control={control}
                        name={`${formNamespace}.city`}
                        rules={{ validate: (v) => (v && v.length > 0) || "Field is required" }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <>
                                <Typeahead
                                    inputProps={{ readOnly: readOnly }}
                                    disabled={disabled}
                                    id="cityAutocomplete"
                                    labelKey="name"
                                    onChange={(selected) => {
                                        onChange(selected)
                                    }}
                                    options={cityList}
                                    placeholder="Choose a city..."
                                    selected={value || []}
                                />
                                {error && <span className="text-danger">{error.message}</span>}
                            </>
                        )}
                    />
                </Form.Group>
            </Col>
            <Col sm={2}>
                <Form.Group controlId="province">
                    <FloatingLabel label="Province/State">
                        <Controller
                            control={control}
                            rules={{ required: "Field is required" }}
                            name={`${formNamespace}.province`}
                            defaultValue=""
                            render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                                <>
                                    <Form.Select
                                        className={readOnly ? "select--readonly" : ""}
                                        disabled={disabled}
                                        value={value}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            clearErrors(`${formNamespace}.province`);
                                            handleProvinceChange(e);
                                        }}
                                        ref={ref}
                                    >
                                        <option value="">Select</option>
                                        {((watch(`${formNamespace}.provinceList`)?.data?.data) || []).map((data, index) => (
                                            <option key={index} value={data.provineStateID}>
                                                {data.provineSateName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {error && <span className="text-danger">{error.message}</span>}
                                </>
                            )}
                        />
                    </FloatingLabel>
                </Form.Group>
            </Col>
            <Col sm={2}>
                <Form.Group>
                    <FloatingLabel label="Country">
                        <Controller
                            control={control}
                            name={`${formNamespace}.country`}
                            rules={{ required: "Field is required" }}
                            render={({
                                field: { value, onChange, ref },
                                fieldState: { error }
                            }) => (
                                <>
                                    <Form.Select
                                        className={readOnly ? "select--readonly" : ""}
                                        value={value}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            clearErrors(`${formNamespace}.country`);
                                            handleCountryChange(e);
                                        }}
                                        ref={ref}
                                        disabled={disabled}
                                    >
                                        <option value="">Select</option>
                                        {((watch(`${formNamespace}.countryList`)?.data?.data) || []).map((data, idx) => (
                                            <option key={idx} value={data.countryId}>
                                                {data.countryname}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {error && <span className="text-danger">{error.message}</span>}
                                </>
                            )}
                        />
                    </FloatingLabel>
                </Form.Group>
            </Col>
        </>
    );
};

export default CityProvinceCountrySelector;
